// # Please ensure understanding of the pricing page public setup, documented here:
// # https://product.hubteam.com/docs/growth-ssm-handbook/docs/dotcom/overview.html
// # before making changes to this file or merging in mothership changes

import { Application } from 'foundations-components/components/Application';
import { createBrowserHistory } from 'history';
import { renderRoot } from 'quartz-core/client';

// I18n
import BasicPublicI18nSetup from 'I18n/init/setup/BasicPublicI18nSetup';
import lang from 'i2l?query=sporks!../lang/en.lyaml';

// Rhumb
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import rhumbConfig from '../rhumb-config.yaml';

// Redux
import { startApp } from 'pricing-pages-ui/actions/AppActions';
import configureStore from 'pricing-pages-ui/utils/configureStore';
import { Provider } from 'react-redux';

// Data fetching client
import { DataFetchingClientProvider } from 'data-fetching-client';
import { dataFetchingClient } from 'pricing-pages-ui/utils/dataFetchingClient';

// Configure the router
import Routes from 'pricing-pages-ui/Routes';
import { Router } from 'react-router-dom';

// import Raven from 'Raven';

import { storeEnvironmentConfig } from 'pricing-pages-ui/actions/ConfigActions';
import { getPrimaryTracker, setPrimaryTracker } from 'usage-tracker-container';
import { COOKIE_ECHO_USER, PHONE_NUMBER, PUBLIC_PRICING_PAGE_DETAILS } from 'pricing-pages-api/EarlyRequesterNames';
import { GET_PHONE_NUMBER } from 'pricing-pages-api/phone-number/queries';
import { GET_PUBLIC_PRICING_PAGE_DETAILS } from 'pricing-pages-api/public-pricing-page-details/queries';
import { getPublicAppLegacyPurchaseMotionsResponse } from 'pricing-pages-api/purchase-motions/getPublicAppLegacyPurchaseMotionsResponse';
import { GET_COOKIE_AUTH } from 'pricing-pages-api/user/queries';
import { AllRoutes, MARKETING } from 'pricing-pages-lib/constants/PricingPageRoutes';
import { getPreferredLanguage } from 'pricing-pages-lib/utils/getPreferredLanguage';
import { getCountryCodeFromLocale } from 'pricing-pages-ui/v2/utils/countryCodeUtils';
import safeLocalStorage from 'pricing-pages-lib/utils/safeLocalStorage';
import { getAbsolutePricingPageUrl } from 'pricing-pages-lib/utils/urls';
import { fetchExperiments } from 'pricing-pages-ui/actions/ExperimentationActions';
import { setPublicFeatureFlags } from 'pricing-pages-ui/actions/FeatureFlagActions';
import { fetchPublicProducts } from 'pricing-pages-ui/actions/ProductsActions';
import { fetchPublicMetaData } from 'pricing-pages-ui/actions/PublicMetaDataActions';
import { fetchPurchaseMotionData } from 'pricing-pages-ui/actions/PurchaseMotionActions';
import { CurrencyContextProvider } from 'pricing-pages-ui/context/CurrencyContext';
import { GlobalLoadingStateContextProvider } from 'pricing-pages-ui/context/GlobalLoadingStateContext';
import { PublicI18nContext } from 'pricing-pages-ui/context/I18nContext';
import { Metrics, setupMetricsFactory } from 'pricing-pages-ui/utils/metrics';
import { getProductLevelFromPath } from 'pricing-pages-ui/utils/urls/publicUtils';
import { createUsageTracker } from 'pricing-pages-ui/v2/utils/tracking/_UsageTracker';
import { trackCommunicationMethodsInteraction } from 'pricing-pages-ui/v2/utils/tracking/trackingEventUtils';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
// const whenIdle = (cb: IdleRequestCallback) => {
//   if (window.hasOwnProperty('requestIdleCallback')) {
//     window.requestIdleCallback(cb);
//   } else {
//     setTimeout(cb, 0);
//   }
// };
export default class App {
  constructor(config) {
    this.store = configureStore();
    this.tracker = createUsageTracker();
    this.config = config;
  }

  // Kicks off the app. This is intentionally not in the constructor.
  async start() {
    setupMetricsFactory('pricing-pages-ui-public-app');
    this.store.dispatch(startApp());
    const i18nManualLang = window.I18N_MANUAL_LANG;
    const langAvailable = BasicPublicI18nSetup.setup({
      lang,
      manualLocaleSettings: i18nManualLang ? {
        locale: i18nManualLang,
        langEnabled: true
      } : undefined
    });
    this.setupEventTracker();
    try {
      await Promise.all([this.store.dispatch(storeEnvironmentConfig(this.config)), langAvailable]);
      this.redirectNonProxied();
      this.startRouter(BasicPublicI18nSetup.provider);
    } catch (__error) {
      await langAvailable;
      // We chain our bootstrapping code with this catch
      // so we can show an Error screen rather than just a blank page
      // A side effect of this is that any errors that bubble up here
      // would be swallowed up unless we re-throw.
      this.startRouter(BasicPublicI18nSetup.provider, true);
      // Raven.captureException(new Error('Failed to bootstrap public app'), {
      //   extra: { error },
      // });
    }
  }
  setupEventTracker() {
    setPrimaryTracker(this.tracker);

    // Required default event properties needs to be set here if we want event tracking on App level - otherwise, it will be undefined and cause usage-tracker-js event error. Properties should not include data from non-blocking fetches as it may not be available when this is called.
    getPrimaryTracker().setProperties({
      isBillingAdmin: false,
      inAppOrPublic: 'public',
      isDotComAuthedRedirect: false
    });
  }
  fetchPricingAndPurchasingData() {
    [fetchPurchaseMotionData({
      getPurchaseMotions: () => getPublicAppLegacyPurchaseMotionsResponse(['MERCH_CARD', 'CALCULATOR', 'HUBSPOT_CREDITS_MERCH_CARD_SMALL', 'HUBSPOT_CREDITS_MERCH_CARD_MEDIUM_LARGE']),
      isPublic: true
    }), fetchPublicProducts(), fetchPublicMetaData(), fetchExperiments()].map(fetchRequest => this.store.dispatch(fetchRequest));
  }
  fetchUserData() {
    const HUBSPOT_DOMAIN = '@hubspot.com';
    dataFetchingClient.query({
      query: GET_COOKIE_AUTH,
      variables: {
        earlyRequestName: COOKIE_ECHO_USER
      }
    }).then(response => {
      var _response$data$cookie, _response$data;
      const email = (_response$data$cookie = (_response$data = response.data) === null || _response$data === void 0 || (_response$data = _response$data.cookieAuth) === null || _response$data === void 0 ? void 0 : _response$data.email) !== null && _response$data$cookie !== void 0 ? _response$data$cookie : '';
      getPrimaryTracker().setProperties({
        isHubSpotter: email.endsWith(HUBSPOT_DOMAIN)
      });
    }).catch(() => {
      // no op
    });
  }
  fetchPublicPricingPageDetails() {
    const browserLocale = getPreferredLanguage().toLowerCase() || 'en-us';
    // Type assertion is safe: value comes from server-side locale detection or hardcoded 'IN' for India domain.
    // API handles unknown country codes gracefully by falling back to defaults.
    const preferredCountryCode = getCountryCodeFromLocale();
    dataFetchingClient.query({
      query: GET_PUBLIC_PRICING_PAGE_DETAILS,
      variables: {
        earlyRequestName: PUBLIC_PRICING_PAGE_DETAILS,
        browserLocale,
        preferredCountryCode
      }
    }).then(response => {
      var _response$data$public, _response$data2, _response$data$public2, _response$data3;
      const activeFeatureFlags = (_response$data$public = (_response$data2 = response.data) === null || _response$data2 === void 0 || (_response$data2 = _response$data2.publicPricingPageDetails) === null || _response$data2 === void 0 ? void 0 : _response$data2.activeFeatureFlags) !== null && _response$data$public !== void 0 ? _response$data$public : [];
      this.store.dispatch(setPublicFeatureFlags({
        activeFeatureFlags
      }));
      const phoneNumber = (_response$data$public2 = (_response$data3 = response.data) === null || _response$data3 === void 0 || (_response$data3 = _response$data3.publicPricingPageDetails) === null || _response$data3 === void 0 ? void 0 : _response$data3.phoneNumber) !== null && _response$data$public2 !== void 0 ? _response$data$public2 : '';
      const preferredLanguage = getPreferredLanguage() || 'en';
      // writeQuery is a synchronous cache write - no error handling needed
      dataFetchingClient.writeQuery({
        query: GET_PHONE_NUMBER,
        data: {
          phoneNumber: {
            phoneNumber
          }
        },
        variables: {
          earlyRequestName: PHONE_NUMBER,
          preferredLanguage
        }
      });
    }).catch(error => {
      Raven.captureException(error, {
        fingerprint: ['failed-to-fetch-public-pricing-page-details']
      });
    });
  }

  /**
   * @see {@link https://git.hubteam.com/HubSpot/conversations-visitor/blob/879af5a94b04d87ddf2f6b4dc794d3421bf6a237/docs/external-js-api/ApiReference.md}
   */
  setupChat() {
    if (window.HubSpotConversations) {
      this.onChatWidgetReady();
    } else {
      window.hsConversationsOnReady = [this.onChatWidgetReady];
    }
  }
  onChatWidgetReady() {
    this.addChatWidgetEventTracking();
  }
  addChatWidgetEventTracking() {
    trackCommunicationMethodsInteraction({
      action: 'chat-conversation-loaded',
      trackingSource: 'public'
    });
    Metrics.counter('live-chat-loaded', {
      location: 'public'
    }).increment();

    /**
     * Conversation started means the user sent a message
     * API reference {@see https://git.hubteam.com/HubSpot/conversations-visitor/blob/master/docs/external-js-api/ApiReference.md}
     */
    window.HubSpotConversations.on('conversationStarted', __payload => {
      trackCommunicationMethodsInteraction({
        action: 'chat-conversation-started',
        trackingSource: 'public',
        conversationId: __payload.conversation.conversationId
      });
      Metrics.counter('live-chat-interaction', {
        location: 'public'
      }).increment();
    });
  }
  redirectNonProxied() {
    const team = safeLocalStorage.getItem('hs-team');
    const isOnApp = !!window.location.hostname.match(/^app(-.*)?\.hubspot(qa)?\.com/);
    if (!isOnApp || team === 'cbx') {
      return;
    }
    const isQA = window.location.hostname.indexOf('qa') >= 0;
    const productLine = window.location.pathname.split('/').find(slug => slug && AllRoutes.includes(slug)) || MARKETING;
    const productLevel = getProductLevelFromPath(window.location.pathname);
    const newUrl = `${getAbsolutePricingPageUrl({
      slug: productLine,
      isAppAuth: false,
      isQA,
      productLevel
    })}${window.location.search}`;
    window.location.href = newUrl;
    // Commenting out for ATs until we get proper QA setup
    // Raven.captureException(
    //   new Error('Unexpected visitor on public app.hubspot.com')
    // );
  }
  startRouter(i18nLoader, __hasError = false) {
    const history = createBrowserHistory({
      basename: this.config.rootUrl
    });
    this.fetchPricingAndPurchasingData();
    this.fetchUserData();
    this.fetchPublicPricingPageDetails();
    try {
      this.setupChat();
    } catch (error) {
      Raven.captureException(error, {
        fingerprint: ['failed-to-setup-chat']
      });
      Metrics.counter('live-chat-failed').increment();
    }
    renderRoot( /*#__PURE__*/_jsx(Application, {
      name: "pricing-pages-ui-public-app",
      children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
        client: dataFetchingClient,
        children: /*#__PURE__*/_jsx(Provider, {
          store: this.store,
          children: /*#__PURE__*/_jsx(PublicI18nContext.Provider, {
            value: {
              i18nLoader: i18nLoader
            },
            children: /*#__PURE__*/_jsx(RhumbProvider, {
              history: historyAdapter(history),
              config: rhumbConfig,
              children: /*#__PURE__*/_jsx(GlobalLoadingStateContextProvider, {
                children: /*#__PURE__*/_jsx(CurrencyContextProvider, {
                  children: /*#__PURE__*/_jsx(Router, {
                    history: history,
                    children: /*#__PURE__*/_jsx(Routes, {})
                  })
                })
              })
            })
          })
        })
      })
    }));
  }
}